import {queries} from '@/shared/integrations/zero'
import {ListPrescribedNamesInput} from '@cypro/engine/queries'
import {PrescribedName} from '@cypro/engine/schema'
import {type GetPageQuery, type GetSingleQuery} from '@cypro/ui/utils/zero-virtualizer'
import {useCallback, useMemo, useState} from 'react'

export type PrescribedNameStartRow = Pick<PrescribedName, 'id' | 'createdAt' | 'name'>

export function useGetInfinitePrescribedNames({
  sort = {dir: 'asc', field: 'name'},
  filters,
  limit = 25,
}: Omit<Partial<ListPrescribedNamesInput>, 'cursor'> = {}) {
  const [search, setSearch] = useState('')

  const listContextParams = useMemo(
    () => ({sort, filters: {status: ['active'] as const, ...filters}, limit, search}),
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [sort?.dir, sort?.field, filters?.search, limit, search]
  )

  const getPageQuery: GetPageQuery<PrescribedName, PrescribedNameStartRow> = useCallback(
    ({limit: pageLimit, start, dir}) => {
      const cursor =
        start?.id && start?.createdAt
          ? {
              id: start.id,
              direction: dir,
              createdAt: start.createdAt,
              name: start.name,
            }
          : undefined

      return {
        query: queries.products.listPrescribedNames({
          cursor,
          limit: pageLimit,
          sort,
          filters: {...filters, ...(search ? {search} : {})},
        }),
      }
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [listContextParams]
  )

  const getSingleQuery: GetSingleQuery<PrescribedName> = useCallback(
    ({id}) => ({
      query: queries.products.getPrescribeNameById(id),
    }),
    []
  )

  const toStartRow = useCallback(
    (row: PrescribedName): PrescribedNameStartRow => ({
      id: row.id,
      createdAt: row.createdAt,
      name: row.name,
    }),
    []
  )

  const transformToSelectItem = useCallback((row: PrescribedName) => ({label: row.name, value: row.id}), [])

  return {
    search,
    onSearchChange: setSearch,
    getPageQuery,
    getSingleQuery,
    toStartRow,
    listContextParams,
    transformToSelectItem,
  }
}
